import ApiService from './ApiService'

export const getApiData = data => {
    var datas
    if (data) {
        datas = {
            page: data.pageIndex,
            page_size: data.pageSize,
        }
        if (data.sort && data.sort.key && data.sort.order) {
            datas.ordering = data.sort.order === 'desc' ? `-${data.sort.key}` : `${data.sort.key}`
        }
        if (data.query) {
            datas.search = data.query
        }
        if (data.selectedTab) {
            datas.status = data.selectedTab
        }
        if (data?.company) {
            datas.company = data?.company
        }
        if (data?.customer) {
            datas.customer = data?.customer
        }
        if (data.id) {
            datas.id = data.id
        }
    }
    return ApiService.get('/api/v1/xero/invoices/', datas)
}

// Traditional invoice management endpoints
export const createApiRecord = data => ApiService.post('/api/v1/xero/invoices/', data)
export const getApiRecord = id => ApiService.get(`/api/v1/xero/invoices/${id}/`)
export const updateApiRecord = (id, data) => ApiService.put(`/api/v1/xero/invoices/${id}/`, data)
export const deleteApiRecord = id => ApiService.delete(`/api/v1/xero/invoices/${id}/`)

// Xero Configuration Management APIs
export const getXeroConfigurations = (params = {}) => {
    return ApiService.get('/api/v1/xero/configurations/', params)
}

export const getActiveXeroConfiguration = () => {
    return ApiService.get('/api/v1/xero/configurations/', { is_active: true, page_size: 1 })
}

// Xero Invoice Creation/Update APIs (using configuration endpoints)
export const createDraftInvoice = (configId, invoiceData) => {
    return ApiService.post(`/api/v1/xero/configurations/${configId}/create_draft_invoice/`, invoiceData)
}

// Xero Invoice Update API (using configuration endpoints)
export const updateDraftInvoice = (configId, invoiceId, invoiceData) => {
    return ApiService.put(`/api/v1/xero/configurations/${configId}/update_draft_invoice/${invoiceId}/`, invoiceData)
}

export const getInvoiceDetails = (configId, invoiceId) => {
    return ApiService.get(`/api/v1/xero/configurations/${configId}/get_invoice_details/`, { invoice_id: invoiceId })
}

export const listInvoiceAttachments = (configId, invoiceId) => {
    return ApiService.get(`/api/v1/xero/configurations/${configId}/list_invoice_attachments/`, { invoice_id: invoiceId })
}

// Document attachment APIs
export const attachDocumentToInvoice = (configId, attachmentData) => {
    const formData = new FormData()
    formData.append('invoice_id', attachmentData.invoice_id)
    formData.append('file', attachmentData.file)
    if (attachmentData.filename) {
        formData.append('filename', attachmentData.filename)
    }
    if (attachmentData.include_online !== undefined) {
        formData.append('include_online', attachmentData.include_online)
    }

    return ApiService.filePost(`/api/v1/xero/configurations/${configId}/attach_document_to_invoice/`, formData)
}

export const downloadInvoiceAttachment = async (configId, invoiceId, attachmentId, filename) => {
    const params = {
        invoice_id: invoiceId,
        attachment_id: attachmentId,
        filename: filename,
    }

    try {
        const response = await ApiService.downloadFile(
            `/api/v1/xero/configurations/${configId}/download_invoice_attachment/`,
            params
        )

        // Create blob URL and trigger download
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' })
        const url = window.URL.createObjectURL(blob)

        // Create temporary link and trigger download
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()

        // Cleanup
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        return response
    } catch (error) {
        throw error
    }
}

// Contact and Account Code APIs
export const getXeroContacts = (params = {}) => {
    return ApiService.get('/api/v1/xero/contacts/', params)
}

export const getXeroAccountCodes = (params = {}) => {
    return ApiService.get('/api/v1/xero/account-codes/', params)
}

export const syncContactsFromXero = tenantId => {
    return ApiService.post('/api/v1/xero/contacts/sync_from_xero/', { tenant_id: tenantId })
}

export const syncAccountCodesFromXero = tenantId => {
    return ApiService.post('/api/v1/xero/account-codes/sync_from_xero/', { tenant_id: tenantId })
}

// Helper function to sync contacts for a configuration (extracts tenant_id from config)
export const syncContactsForConfiguration = configuration => {
    if (!configuration.tenant_id) {
        return Promise.reject(new Error('Configuration does not have a tenant_id. Please authorize first.'))
    }
    return syncContactsFromXero(configuration.tenant_id)
}

// Helper function to sync account codes for a configuration (extracts tenant_id from config)
export const syncAccountCodesForConfiguration = configuration => {
    if (!configuration.tenant_id) {
        return Promise.reject(new Error('Configuration does not have a tenant_id. Please authorize first.'))
    }
    return syncAccountCodesFromXero(configuration.tenant_id)
}

// Combined sync function for both contacts and account codes
export const syncAllDataForConfiguration = async configuration => {
    if (!configuration.tenant_id) {
        return Promise.reject(new Error('Configuration does not have a tenant_id. Please authorize first.'))
    }

    // Run both syncs in parallel for faster performance
    const results = await Promise.allSettled([
        syncContactsFromXero(configuration.tenant_id),
        syncAccountCodesFromXero(configuration.tenant_id),
    ])

    const contactsResult = results[0]
    const accountCodesResult = results[1]

    // Return combined results
    return {
        contacts: {
            success: contactsResult.status === 'fulfilled',
            data: contactsResult.status === 'fulfilled' ? contactsResult.value : null,
            error: contactsResult.status === 'rejected' ? contactsResult.reason : null,
        },
        accountCodes: {
            success: accountCodesResult.status === 'fulfilled',
            data: accountCodesResult.status === 'fulfilled' ? accountCodesResult.value : null,
            error: accountCodesResult.status === 'rejected' ? accountCodesResult.reason : null,
        },
        overallSuccess: contactsResult.status === 'fulfilled' && accountCodesResult.status === 'fulfilled',
    }
}

// Helper function to get customers data for dropdowns
export const getCustomersForDropdown = (configurationId = null, companyId = null) => {
    const params = { is_customer: true }
    if (configurationId) {
        params.configuration = configurationId
    } else if (companyId) {
        params.company = companyId
    }

    return getXeroContacts(params).then(response => {
        if (response.data && response.data.results) {
            return response.data.results.map(contact => ({
                label: contact.name,
                value: contact.contact_id || contact.id,
                data: contact,
            }))
        }
        return []
    })
}

// Helper function to get account codes for dropdowns - also update to support configuration
export const getAccountCodesForDropdown = (configurationId = null, companyId = null) => {
    const params = { is_active: true }
    if (configurationId) {
        params.configuration = configurationId
    } else if (companyId) {
        params.company = companyId
    }

    return getXeroAccountCodes(params).then(response => {
        if (response.data && response.data.results) {
            return response.data.results.map(account => ({
                label: `${account.code} - ${account.name}`,
                value: account.code,
                data: account,
            }))
        }
        return []
    })
}

// Tax types (common Xero tax types)
export const getTaxTypesForDropdown = () => {
    return [
        { label: 'No Tax', value: 'NONE' },
        { label: 'GST (10%)', value: 'OUTPUT2' },
        { label: 'GST Free', value: 'EXEMPTOUTPUT' },
        { label: 'GST on Income', value: 'OUTPUT' },
        { label: 'GST on Capital', value: 'CAPOUTPUT' },
        { label: 'GST Free Capital', value: 'EXEMPTCAPITAL' },
    ]
}

// Trigger full sync of all Xero data for a company
export const syncAllXeroData = companyId => {
    return ApiService.post('/api/v1/xero/sync/all/', { company_id: companyId })
}

// Get Maestro-created draft invoices
export const getMaestroInvoices = data => {
    const params = {
        page: data?.pageIndex || 1,
        page_size: data?.pageSize || 10,
    }

    if (data?.sort && data.sort.key && data.sort.order) {
        params.ordering = data.sort.order === 'desc' ? `-${data.sort.key}` : `${data.sort.key}`
    }

    if (data?.query) {
        params.search = data.query
    }

    if (data?.company) {
        params.company = data.company
    }

    if (data?.customer) {
        params.customer = data.customer
    }

    return ApiService.get('/api/v1/xero/invoices/maestro_invoices/', params)
}
